import moment from 'moment';
import React, {useState, FC, RefObject, Key} from 'react';

import {customTableUtils} from 'components/@common/widgets/custom-table';
import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {IColumnSortData} from 'components/@common/widgets/custom-table/table-column-menu';
import {Spinner} from 'components/spinner';
import {DATE_DMY} from 'shared/constants/date-format';
import {useAfterEffect} from 'shared/hooks/use-after-effect';
import {usePagination} from 'shared/hooks/use-pagination';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {TStateSetter} from 'shared/types/generics';
import {IPaginationQuery, ISortQuery} from 'shared/types/requests';
import {getTablePaginationDefaultConfig} from 'shared/utils';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {AIS_SUMMARY_TRANSACTION_PROTOCOL_ROLE_SECTION, DEFAULT_TABLE_PAGE_SIZE} from '../../model/constants';
import {ISummaryTransactionProtocolDto} from '../../model/types';
import {EditRowModalOpener} from '../edit-row-modal-opener/edit-row-modal-opener';

const {
    setHeaderStyles,
} = customTableUtils;

interface ITransactionProtocolTableProps {
  dataSource: ISummaryTransactionProtocolDto[];
  recordsTotal: number;
  selectedRowKeys: Key[];
  tableRef: RefObject<HTMLDivElement>;
  setSelectedRowKeys: TStateSetter<Key[]>;
  setSortQuery: TStateSetter<ISortQuery | undefined>;
  setPaginationQuery: TStateSetter<IPaginationQuery | undefined>;
  refetchTableData: () => {};
}
export interface IAisDocumentsRegisterTableRef {
    setSortData: TStateSetter<IColumnSortData>;
}

export const TransactionProtocolTable: FC<ITransactionProtocolTableProps> = (
    {
        dataSource,
        recordsTotal,
        selectedRowKeys,
        tableRef,
        setSelectedRowKeys,
        setSortQuery,
        setPaginationQuery,
        refetchTableData,
    }: ITransactionProtocolTableProps,
) => {
    const [sortData, setSortData] = useState<IColumnSortData>({
        sort: {},
    });

    const {hasUserRoleFunction, isLoadingUserFunctions} = useUserRoleFunctions({
        sections: [AIS_SUMMARY_TRANSACTION_PROTOCOL_ROLE_SECTION],
    });

    const {
        current, pageSize, handleChange,
    } = usePagination({
        pageDefault: 1,
        pageSizeDefault: DEFAULT_TABLE_PAGE_SIZE,
        onChange: (paginationCurrent, paginationPageSize) => setPaginationQuery({
            paginationCurrent,
            paginationPageSize,
        }),
    });

    const {
        customTableJSX,
        tableColumnSortDataAdaptedForRequest,
        lastSortedColumnData,
    } = useCustomTable({
        tableColumnSortData: sortData,
        setTableColumnSortData: setSortData,
        isSingleColumnSort: true,
        pagination: {
            ...getTablePaginationDefaultConfig({defaultPageSize: 20}),
            pageSize,
            current,
            total: recordsTotal,
            onChange: handleChange,
        },
        rowKey: 'id',
        rowSelection: {
            selectedRowKeys,
            onChange: selectedTableRowKeys => setSelectedRowKeys(selectedTableRowKeys),
        },
        className: 'mt-5',
        locale: {
            emptyText: (
                <div style={{marginTop: 30}}>
                    {(() => {
                        if (!hasUserRoleFunction(EUserRoleFunction.VIEW) && isLoadingUserFunctions) {
                            return <div><Spinner tip="Загрузка..." /></div>;
                        }
                        if (!hasUserRoleFunction(EUserRoleFunction.VIEW) && !isLoadingUserFunctions) {
                            return 'Недостаточно прав для просмотра данных';
                        }
                        return 'Для формирования данных необходимо задать параметры';
                    })()}
                </div>
            ),
        },
        dataSource,
        onColumn: (col, i) => ({
            ...setHeaderStyles({
                textAlign: 'center',
            }),
            ...col,
            withCustomSort: i >= 1,
            withHideColumnButton: false,
        }),
        bordered: true,
        columns: [
            {
                render: (_, row) => (
                    hasUserRoleFunction(EUserRoleFunction.CREATE)
                        && row?.statusCode?.lookupCode === 'NEW'
                        && (
                            <EditRowModalOpener
                                initialRowValues={row}
                                refetchTableData={refetchTableData}
                            />
                        )
                ),
            },
            {
                title: '№',
                key: 'id',
                dataIndex: 'id',
                align: 'center',
            },
            {
                title: 'Идентификатор сводного протокола операций',
                dataIndex: 'summaryTransactionsProtocolId',
                align: 'center',
            },
            {
                title: 'Идентификатор загрузки',
                dataIndex: 'loadId',
                align: 'center',
            },
            {
                title: 'Статус',
                dataIndex: 'statusCode',
                align: 'center',
                render: v => v?.meaning,
            },
            {
                title: 'Дата начала отчетного периода',
                dataIndex: 'startPeriodDate',
                align: 'center',
                render: v => v !== null && moment(v).format(DATE_DMY),
            },
            {
                title: 'Дата окончания отчетного периода',
                dataIndex: 'endPeriodDate',
                align: 'center',
                render: v => v !== null && moment(v).format(DATE_DMY),
            },
            {
                title: 'Общее количество операций',
                dataIndex: 'totalTransactionsAmt',
                align: 'center',
            },
            {
                title: 'Количество операций с наличием первичных учетных документов',
                dataIndex: 'transactionsWithDocs',
                align: 'center',
            },
            {
                title: 'Идентификатор пользователя, создавшего запись',
                dataIndex: 'createdBy',
                align: 'center',
            },
            {
                title: 'Дата создания',
                dataIndex: 'createdDate',
                align: 'center',
                render: v => v !== null && moment(v).format(DATE_DMY),
            },
            {
                title: 'Идентификатор пользователя, изменившего запись',
                dataIndex: 'lastModifiedBy',
                align: 'center',
            },
            {
                title: 'Дата изменения',
                dataIndex: 'lastModifiedDate',
                align: 'center',
                render: v => v !== null && moment(v).format(DATE_DMY),
            },
        ],
    });

    useAfterEffect(() => {
        setSortQuery(lastSortedColumnData);
        return () => {};
    }, [tableColumnSortDataAdaptedForRequest]);

    return <div ref={tableRef}>{customTableJSX}</div>;
};
